"use client";

import { useState, useEffect } from "react";
import NewSelectInput from "../../../components/NewSelectInput";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import {
  IconStack,
  IconBox,
  IconBriefcase,
  IconTrash,
  IconPlus,
  IconRoute,
  IconClipboardText,
  IconPrinter,
} from "@tabler/icons-react";
import { useLanguage } from "../../../context/LanguageContext";

type ContainerType = "PALLET" | "BOX" | "BAG";

type ContainerEntry = {
  uId: string;
  type: ContainerType;
  id: string;
  awbs: string;
};

const CONTAINER_META = {
  PALLET: { label: "Pallet", icon: <IconStack size={16} /> },
  BOX: { label: "Box", icon: <IconBox size={16} /> },
  BAG: { label: "Bag", icon: <IconBriefcase size={16} /> },
};

const generateContainerId = (type: ContainerType) => {
  return `${type.slice(0, 3)}-${Math.random()
    .toString(36)
    .substring(2, 8)
    .toUpperCase()}`;
};

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <div className="p-1 rounded-lg bg-[#FBD037]/20">
      <Icon size={14} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const LinehaulRequestModal: React.FC<{
  register: any;
  errors: any;
  setValue: any;
  watch: any;
  step?: any;
}> = ({
  register,
  errors,
  setValue,
  watch,
  step,
}) => {
  const { data: warehouses } = useWarehouseListData();
  const { t } = useLanguage();

  const [containers, setContainers] = useState<ContainerEntry[]>([
    { uId: Date.now().toString(), type: "PALLET", id: "", awbs: "" },
  ]);

  const [activeIndex, setActiveIndex] = useState(0);

  const fromWarehouseId = watch("fromWarehouseId");
  const currentContainer = containers[activeIndex] || containers[0];

  const totalItems = containers.reduce(
    (acc, curr) => acc + curr.awbs.split("\n").filter(Boolean).length,
    0,
  );

  const handleAwbChange = (text: string) => {
    const lines = text.split(/[\s,\n]+/);

    const otherAwbs = containers
      .filter((_, idx) => idx !== activeIndex)
      .flatMap((c) => c.awbs.split("\n"))
      .filter(Boolean);

    const uniqueLines = Array.from(new Set(lines.filter(Boolean))).filter(
      (line) => !otherAwbs.includes(line),
    );

    const updated = [...containers];
    updated[activeIndex].awbs = uniqueLines.join("\n");

    if (uniqueLines.length > 0 && !updated[activeIndex].id) {
      updated[activeIndex].id = generateContainerId(updated[activeIndex].type);
    }

    setContainers(updated);
  };

  const addNewContainer = (type: ContainerType) => {
    const newEntry: ContainerEntry = {
      uId: Date.now().toString(),
      type,
      id: "",
      awbs: "",
    };

    setContainers((prev) => [...prev, newEntry]);
    setActiveIndex(containers.length);
  };

  const removeContainer = (idx: number) => {
    if (containers.length === 1) return;

    const updated = containers.filter((_, i) => i !== idx);
    setContainers(updated);
    setActiveIndex(idx > 0 ? idx - 1 : 0);
  };

  useEffect(() => {
    setValue("linehaul_load_list", containers);
  }, [containers, setValue]);

  return (
    <div className="w-full   bg-white space-y-2.5 text-[#1F2937]">
      {/* Route Details */}
      <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-2.5 transition-all duration-300 space-y-1.5">
        <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
          <SectionHeader icon={IconRoute} title={t("Route Details")} className="" />
          <div className="flex flex-wrap gap-1">
            {(["PALLET", "BOX", "BAG"] as ContainerType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => addNewContainer(type)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold border border-yellow-200 bg-yellow-50/70 hover:bg-yellow-100/70 active:scale-[0.98] transition-all text-slate-800"
              >
                <IconPlus size={10} />
                {CONTAINER_META[type].label}
              </button>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="grid grid-cols-2 gap-4">
          <NewSelectInput
            className=""
            name="fromWarehouseId"
            label={t("Origin Hub")}
            placeholder={t("Origin Hub")}
            selectOptions={
              warehouses?.warehouses?.map((w: any) => ({
                id: w?.id,
                value: w?.id,
                text: w?.name,
              })) || []
            }
            register={register}
            setValue={(val: string) => setValue("fromWarehouseId", val)}
            value={fromWarehouseId}
          />

          <NewSelectInput
            className=""
            name="toWarehouseId"
            label={t("Destination Hub")}
            placeholder={t("Destination Hub")}
            selectOptions={
              warehouses?.warehouses
                ?.filter((w: any) => w?.id !== fromWarehouseId)
                ?.map((w: any) => ({
                  id: w?.id,
                  value: w?.id,
                  text: w?.name,
                })) || []
            }
            register={register}
            setValue={(val: string) => setValue("toWarehouseId", val)}
            value={watch("toWarehouseId")}
          />

          <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-clean pr-1 col-span-2">
            {containers.map((c, idx) => {
              const itemCount = c.awbs.split("\n").filter(Boolean).length;
              const isActive = activeIndex === idx;

              return (
                <div
                  key={c.uId}
                  onClick={() => setActiveIndex(idx)}
                  className={`flex-shrink-0 flex items-center gap-2 rounded-xl border px-2.5 py-1.5 cursor-pointer transition-all select-none ${
                    isActive
                      ? "border-yellow-400 bg-yellow-50/50 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`${isActive ? "text-yellow-600" : "text-gray-400"}`}
                  >
                    {CONTAINER_META[c.type].icon}
                  </span>
                  <div className="text-left">
                    <div className="">
                      <span className="font-bold text-[11px] text-[#212121]">
                        {" "}
                        {CONTAINER_META[c.type].label} {idx + 1}
                      </span>{" "}
                      :{" "}
                      <span className="text-[10px] text-[#212121]/70">
                        {t("Seal No.")?.replace(".", "")}. {c.id || "CEN-95925634"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-[9px] text-gray-400">
                        {itemCount} items
                      </p>

                      <IconPrinter size={14} />
                    </div>
                  </div>
                  {containers.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeContainer(idx);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                    >
                      <IconTrash size={11} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: Container Manager */}
      {/* <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-2.5 transition-all duration-300 space-y-1.5">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <SectionHeader icon={IconStack} title="Containers" />
        <div className="space-y-2.5"> 
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
            <div className="flex flex-wrap gap-1">
              {(["PALLET", "BOX", "BAG"] as ContainerType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => addNewContainer(type)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold border border-yellow-200 bg-yellow-50/70 hover:bg-yellow-100/70 active:scale-[0.98] transition-all text-slate-800"
                >
                  <IconPlus size={10} />
                  {CONTAINER_META[type].label}
                </button>
              ))}
            </div>

            <div className="text-[10px] text-gray-500 font-semibold shrink-0">
              Total:{" "}
              <span className="font-bold text-gray-900">{totalItems}</span>
            </div>
          </div>
 
          <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-clean pr-1">
            {containers.map((c, idx) => {
              const itemCount = c.awbs.split("\n").filter(Boolean).length;
              const isActive = activeIndex === idx;

              return (
                <div
                  key={c.uId}
                  onClick={() => setActiveIndex(idx)}
                  className={`flex-shrink-0 flex items-center gap-2 rounded-xl border px-2.5 py-1.5 cursor-pointer transition-all select-none ${
                    isActive
                      ? "border-yellow-400 bg-yellow-50/50 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`${isActive ? "text-yellow-600" : "text-gray-400"}`}
                  >
                    {CONTAINER_META[c.type].icon}
                  </span>
                  <div className="text-left">
                    <div className="font-bold text-[11px] text-gray-800">
                      {CONTAINER_META[c.type].label} {idx + 1}
                    </div>
                    <div className="text-[9px] text-gray-400 font-mono">
                      {itemCount} items
                    </div>
                  </div>
                  {containers.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeContainer(idx);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                    >
                      <IconTrash size={11} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div> */}

      {/* Section 3: Container Scan Area */}
      {currentContainer && (
        <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-2.5 transition-all duration-300 space-y-1.5">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <SectionHeader
            icon={IconClipboardText}
            title={`${CONTAINER_META[currentContainer.type].label} ${t("Details")}`}
          />
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[11px] text-gray-500 border-b border-gray-100 pb-1.5">
              <span>
                {t("Container ID")}:{" "}
                <span className="font-mono font-semibold text-gray-700">
                  {currentContainer.id || t("paste or scan awb numbers (one per line)")}
                </span>
              </span>
              <span>
                {t("Items")}:{" "}
                <span className="font-semibold text-gray-900">
                  {currentContainer.awbs.split("\n").filter(Boolean).length}
                </span>
              </span>
            </div>

            <textarea
              className="w-full h-[80px] min-h-[70px] rounded-xl border border-gray-200 bg-white p-2.5 font-mono text-xs outline-none resize-none focus:border-yellow-400 transition-colors"
              placeholder={t("paste or scan awb numbers (one per line)")}
              value={currentContainer.awbs}
              onChange={(e) => handleAwbChange(e.target.value)}
            />

            <p className="text-[10px] text-gray-400 leading-tight">
              💡 Duplicate AWBs are automatically prevented. Reassigning an
              existing AWB will move it to the current container.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinehaulRequestModal;
